"use client";

import ArticleCoverHeader from "@/components/ArticleCoverHeader";
import CallToAction from "@/components/CallToAction";
import HighlightTitle from "@/components/HighlightTitle";
import Page from "@/components/Page";
import Section from "@/components/ui/Section";
import { Card, CardContent } from "@/components/ui/card";
import { sortArticlesByDate } from "@/lib/articles";
import { useIsEditing } from "@/lib/tina-helpers";
import { cn, tinaField, useTina } from "@/lib/utils";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import Link from "next/link";
dayjs.extend(advancedFormat);

function BlogHeader({ tinaFieldData, title, subtitle }) {
  return (
    <Section as="header" wrapperClassName="bg-muted" backgroundPattern={false}>
      <div className="relative flex flex-col items-center gap-12 p-6 lg:p-12">
        <div className="absolute left-0 top-0 h-10 w-16 border-[3px_0_0px_3px] border-primary" />
        <div className="absolute bottom-0 right-0 h-10 w-16 border-[0px_3px_3px_0px] border-primary" />
        <div className="flex max-w-[840px] flex-col items-center text-center">
          <HighlightTitle as="h1" className="h0" content={title} data-tina-field={tinaField(tinaFieldData, "title")} />
          {subtitle && (
            <p className="p-lg mt-6 whitespace-pre-line" data-tina-field={tinaField(tinaFieldData, "subtitle")}>
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </Section>
  );
}

export default function BlogPageClient({ postsQuery, postsVariables, postsData, ...props }) {
  const { isEditing } = useIsEditing();
  const { data: postsResult } = useTina({
    query: postsQuery,
    variables: postsVariables,
    data: postsData,
  });
  const posts = sortArticlesByDate(postsResult?.postConnection?.edges || []);
  const visiblePosts = posts.filter(({ node }) => node?.published || isEditing);
  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  const blogPage = data?.blogPage;
  const { data: homeData } = useTina({
    query: props.homeQuery,
    variables: props.homeVariables,
    data: props.homeData,
  });
  const cta = homeData?.home?.cta;

  return (
    <Page defaultTinaField={blogPage}>
      <BlogHeader tinaFieldData={blogPage} title={blogPage?.title} subtitle={blogPage?.subtitle} />
      <Section className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {visiblePosts.map(({ node }) => {
          const isPublished = node.published;

          return (
            <Link href={`/blog/${node._sys.filename}`} key={node._sys.filename} className="relative">
              <Card
                className={cn(
                  "relative h-full bg-white border border-[#ebebeb] overflow-hidden p-0 transition-all duration-300 hover:scale-[1.02]",
                  !isPublished ? "opacity-60" : "opacity-100"
                )}
              >
                <CardContent className="p-0">
                  <ArticleCoverHeader title={node.title} />
                  <div className="space-y-2 p-6">
                    <div className="text-sm text-muted-foreground">{node.date ? dayjs(node.date).format("Do MMMM YYYY") : null}</div>
                    <p className="text-base! font-normal! line-clamp-3 leading-[170%]!">{node.description}</p>
                  </div>
                </CardContent>
              </Card>
              {!isPublished && (
                <div className="absolute inset-0 z-10 flex items-center justify-center">
                  <div className="-rotate-45 translate-y-8 text-5xl font-bold text-primary">DRAFT</div>
                </div>
              )}
            </Link>
          );
        })}
      </Section>
      {/* {cta && <CallToAction tinaFieldData={homeData.home.cta} {...cta} />} */}
    </Page>
  );
}
